import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import Topbar from '../../../PulseSurvey/Topbar';
import B2BOverview from '../B2BOverview';
import { Api } from '../../../../api';
import { CompanyTypeData } from '../../../../utils/constants';
import { getLocalStorageItem } from '../../../../utils/helper';
import {
  Panel,
  PanelEmpty,
  StandardDropdown,
  CHART_BASE,
  CHART_GRID,
  CHART_COLORS
} from '../../../../component/common/designSystem';

/**
 * Business Hub → B2B dashboard, on the design system.
 *
 * Two parts: the company pipeline (B2BOverview, shared with the Earnings page)
 * and the overall-company graph, which shows one company's — or one
 * industry's — wellbeing percentage by month.
 *
 * Changed from the old page, deliberately:
 *  - The year picker offered 2000 to 2099. It now offers the years there can
 *    be data for.
 *  - A button that did nothing on mobile (no handler) is gone.
 *  - The survey chart is gone. Its data request had been commented out, so it
 *    rendered empty for every admin every time; an always-empty panel reads as
 *    "no surveys", which is not what was true.
 */

const YEARS_SHOWN = 6;

const BTBDashboard = ({ topbarShow = true }) => {
  const userData = useMemo(() => {
    const raw = getLocalStorageItem('userData');
    return raw ? JSON.parse(raw) : {};
  }, []);

  const [companies, setCompanies] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  // A company and an industry are alternative questions — picking one clears
  // the other, exactly as the old page's handler did.
  const [companyId, setCompanyId] = useState('');
  const [category, setCategory] = useState('');
  const [graph, setGraph] = useState({ labels: [], values: [] });
  const [graphLoading, setGraphLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Api.getCompanyList('', '')
      .then((res) => {
        if (cancelled || res?.data?.meta?.code !== 1) return;
        const rows = res.data.data || [];
        setCompanies(rows);
        // Open on the first company rather than an empty graph.
        if (rows.length) setCompanyId(rows[0]._id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId && !category) return undefined;

    let cancelled = false;
    setGraphLoading(true);
    Api.getOverallCompanyGraph(companyId, year, category)
      .then((res) => {
        if (cancelled) return;
        setGraph({
          labels: res?.data?.labels || [],
          values: (res?.data?.overallPercentage || []).map((value) => Number(value) || 0)
        });
      })
      .catch(() => {
        if (!cancelled) setGraph({ labels: [], values: [] });
      })
      .finally(() => {
        if (!cancelled) setGraphLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, category, year]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: YEARS_SHOWN }, (_, index) => {
    const value = currentYear - index;
    return { label: String(value), value };
  });

  const companyOptions = [
    { label: 'All companies', value: '' },
    ...companies.map((company) => ({ label: company.company_name, value: company._id }))
  ];

  const categoryOptions = [
    { label: 'All industries', value: '' },
    ...(CompanyTypeData || []).map((type) => ({ label: type, value: type }))
  ];

  const chartOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: graph.labels },
    yaxis: { max: 100, labels: { formatter: (value) => `${Math.round(value)}%` } },
    tooltip: { y: { formatter: (value) => `${Math.round(value)}%` } }
  };

  const subjectLabel = category
    ? `${category} companies`
    : companies.find((company) => company._id === companyId)?.company_name || 'Choose a company';

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>B2B Dashboard | Shoorah Admin</title>
      </Helmet>

      {topbarShow ? (
        <Topbar title={userData?.name ? `Hi ${userData.name}` : 'B2B Dashboard'} />
      ) : null}

      <div className="lg:mt-4 flex flex-col gap-6">
        <B2BOverview title="Companies" showDownload showInsights />

        <Panel
          title="Overall company wellbeing"
          subtitle={`${subjectLabel} · ${year}`}
          action={
            <div className="flex flex-wrap gap-2">
              <StandardDropdown
                label="Year"
                options={yearOptions}
                value={year}
                onChange={setYear}
                variant="secondary"
                align="right"
              />
              <StandardDropdown
                label="Company"
                options={companyOptions}
                value={companyId}
                onChange={(value) => {
                  setCompanyId(value);
                  setCategory('');
                }}
                variant="secondary"
                align="right"
              />
              <StandardDropdown
                label="Industry"
                options={categoryOptions}
                value={category}
                onChange={(value) => {
                  setCategory(value);
                  setCompanyId('');
                }}
                variant="secondary"
                align="right"
              />
            </div>
          }
        >
          {graph.values.some((value) => value > 0) ? (
            <Chart
              type="bar"
              height={320}
              options={chartOptions}
              series={[{ name: 'Overall wellbeing', data: graph.values }]}
            />
          ) : (
            <PanelEmpty>
              {graphLoading
                ? 'Loading…'
                : companyId || category
                ? 'No wellbeing data for this selection yet.'
                : 'Choose a company or an industry.'}
            </PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

BTBDashboard.propTypes = {
  topbarShow: PropTypes.bool
};

export default BTBDashboard;
